import { useQuery } from '@tanstack/react-query'
import { apiFetch } from './client'

export interface AuthorizeContext {
  client: {
    client_id: string
    name: string
    logo_uri: string | null
    client_uri: string | null
    redirect_uri: string
    redirect_host: string
  }
  requested_scopes: string[]
  memberships: Array<{
    org_id: string
    name: string
    role: string
  }>
  active_org_id: string | null
}

export function useAuthorizeContext(requestId: string | null) {
  return useQuery<AuthorizeContext>({
    queryKey: ['oauth', 'authorize-context', requestId],
    queryFn: () =>
      apiFetch<AuthorizeContext>(
        `/api/oauth/authorize/context?request_id=${encodeURIComponent(requestId ?? '')}`,
      ),
    enabled: !!requestId,
    retry: false,
    staleTime: 0,
  })
}

// Human-readable scope labels. Keep in sync with ALL_SCOPES on the backend.
export const SCOPE_LABELS: Record<string, { title: string; description: string }> = {
  'mcp:search': {
    title: 'Browse Voysi AI operations',
    description: 'Discover available operations by keyword and domain.',
  },
  'mcp:execute': {
    title: 'Run Voysi AI operations',
    description: 'Execute operations on your behalf — including calls and campaigns.',
  },
  'contacts:read': {
    title: 'Read your contacts',
    description: 'View names, phone numbers, and notes on contacts in this org.',
  },
  'contacts:write': {
    title: 'Manage your contacts',
    description: 'Create, update, and delete contacts on your behalf.',
  },
  'calls:read': {
    title: 'Read call history',
    description: 'View past calls, transcripts, and outcomes.',
  },
  'calls:write': {
    title: 'Place calls',
    description: 'Trigger outbound phone calls on your behalf.',
  },
  'campaigns:read': {
    title: 'Read campaigns',
    description: 'View campaign lists and their contacts.',
  },
  'campaigns:write': {
    title: 'Manage campaigns',
    description: 'Upload, update, and delete campaign lists.',
  },
  'simulations:read': {
    title: 'Read simulations',
    description: 'View simulation campaigns and results.',
  },
  'simulations:write': {
    title: 'Run simulations',
    description: 'Trigger AI-to-AI simulation calls on your behalf.',
  },
  'telemetry:read': {
    title: 'Read telemetry',
    description: 'Stream live call events and historical telemetry.',
  },
  'usage:read': {
    title: 'Read usage and billing',
    description: 'View per-org usage events, costs, and spend caps.',
  },
}
